<template>
	<v-container fluid>
		<!-- delete dialog -->
		<bee-dialog
			v-model="deleteDialog"
			max-width="350"
			:persistent="true"
			:title="$t('CurrenciesPage.delete-currency')"
			:message="$t('CurrenciesPage.are-you-sure-you-want-to-delete-the-currency')"
			@click:confirm="confirmDeleteCurrency"
			@click:close="currencyId = null; deleteDialog = false;"
		></bee-dialog>
		<!-- dialog -->
		<v-dialog
			v-model="dialog"
			persistent
			max-width="400"
			content-class="rounded-medium"
		>
			<v-form ref="form" @submit.prevent="save">
				<v-card>
					<v-card-title>
						{{
							currencyId
								? $t('CurrenciesPage.edit-currency')
								: $t('CurrenciesPage.add-currency')
						}}
					</v-card-title>
					<v-card-text class="pb-2">
						<v-row dense class="justify-center">
							<!-- currency name -->
							<v-col cols="12">
								<v-text-field
									v-model="currency.name"
									:label="$t('CurrenciesPage.currency-name')"
									outlined
									dense
									:rules="[rules.required]"
								/>
							</v-col>

							<!-- currency symbol -->
							<v-col cols="12">
								<v-text-field
									v-model="currency.symbol"
									:label="$t('CurrenciesPage.currency-symbol')"
									outlined
									dense
									:rules="[rules.required]"
								></v-text-field>
							</v-col>
							
							<!-- currency value -->
							<v-col cols="12" v-if="!currencyId">
								<bee-numeric-field
									v-model="currency.latestExchangeRecord"
									:label="$t('CurrenciesPage.equivalent-price-with-system-currency')"
									floating-point-precision="unlimited"
									context="currency"
									:action-name="actions.CREATE"
									:rules="[rules.required, rules.money]"
								></bee-numeric-field>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-btn
							text
							color="success"
							type="submit"
							:loading="submitLoading"
						>
							{{$t('CurrenciesPage.save')}}
						</v-btn>
						<v-btn
							text
							color="danger-color"
							:disabled="submitLoading"
							@click="dialog = false"
						>
							{{$t('CurrenciesPage.cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- change exchange -->
		<v-dialog
			v-model="updateEquivalentPriceDialog"
			persistent
			max-width="400"
			content-class="rounded-medium"
		>
			<v-form ref="equivalentPriceForm" @submit.prevent="createExchangeRecord">
				<v-card>
					<v-card-title>
						{{$t('CurrenciesPage.update-currency-price')}}
					</v-card-title>
					<v-card-text>
						<v-row dense>
							<!-- currency-name -->
							<v-col cols="12">
								<v-text-field
									v-model="currency.name"
									:label="$t('CurrenciesPage.currency-name')"
									outlined
									dense
									disabled
								></v-text-field>
							</v-col>

							<!-- equivalent price -->
							<v-col cols="12">
								<bee-numeric-field
									v-model="exchangeValue"
									:label="$t('CurrenciesPage.equivalent-price-with-system-currency')"
									floating-point-precision="unlimited"
									context="currency"
									action-name="edit"
									:rules="[rules.required, rules.money]"
								></bee-numeric-field>
							</v-col>

							<!-- update currency date -->
							<v-col cols="12">
								<bee-date-picker
									v-model="createdAt"
									:label="$t('CurrenciesPage.update-currency-date')"
									context="currency"
									action-name="create"
									:rules="[rules.date]"
								></bee-date-picker>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions class="px-5">
						<v-btn
							text
							color="success"
							:loading="updateEquivalentPriceLoading"
							type="submit"
						>
							{{$t('CurrenciesPage.save')}}
						</v-btn>
						<v-btn
							text
							color="danger-color"
							:disabled="updateEquivalentPriceLoading"
							@click="updateEquivalentPriceDialog = false;$refs.equivalentPriceForm.reset();"
						>
							{{$t('CurrenciesPage.cancel')}}
						</v-btn>
						<v-spacer></v-spacer>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- exchange records history -->
		<v-dialog
			v-model="exchangeRecordsHistoryDialog"
			persistent
			max-width="600"
			content-class="rounded-medium"
		>
			<v-card flat>
				<v-card-title>
					{{
						$t('CurrenciesPage.currency-equivalent-rate-update-record-for') + ' ' +
						(currencyId ? getCurrencyById(currencyId)?.name : '')
					}}
				</v-card-title>
				<v-card-text class="pt-0">
					<bee-handy-table
						:headers="historyHeaders"
						:items="exchangeRecords"
						:items-per-page="-1"
						height="400px"
						fixed-header
						hide-default-footer
						:data-table-props="{ disableSort: true }"
						:loading="exchangeRecordsHistoryLoading"
						dense
					></bee-handy-table>
				</v-card-text>
				<v-card-actions class="pt-0">
					<v-btn
						color="greenDarken1"
						text
						@click="exchangeRecordsHistoryDialog = false"
					>
						{{$t('CurrenciesPage.ok')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- top side -->
		<v-row dense align="center" class="mb-2">
			<v-col cols="auto">
				<slot name="breadcrumbs"></slot>
			</v-col>
			<v-spacer></v-spacer>
			<v-col cols="auto">
				<v-btn
					color="success"
					:disabled="isFileLocked"
					@click="dialog = true"
				>
					<v-icon class="me-1">mdi-plus-circle-outline</v-icon>
					{{$t('CurrenciesPage.add-currency')}}
				</v-btn>
			</v-col>
		</v-row>
		
		<!-- table -->
		<bee-handy-table
			:headers="headers"
			:items="loading ? [] : currencies"
			:items-per-page="20"
			:height="$vuetify.breakpoint.height - 180"
			fixed-header
			dense
			pagination-on-scroll
			:row-highlight-expression="v => configs.financial_file ? configs.financial_file.default_currency_id === v.id : v.is_main_currency"
			:loading="loading"
			:data-table-props="{ disableSort: true }"
			hide-shown-rows-text="auto"
		>
			<template v-slot:item.actions="{ item }">
				<!-- edit -->
				<tooltip v-if="!isFileLocked" top :text="$t('tooltip.edit')">
					<v-btn
						height="34"
						width="38"
						min-width="38"
						color="edit-color white--text"
						class="me-1"
						@click="currencyId = item.id; dialog = true;"
					>
						<v-icon>mdi-pencil</v-icon>
					</v-btn>
				</tooltip>

				<!-- cached -->
				<tooltip v-if="!isFileLocked" top :text="$t('tooltip.cached')">
					<v-btn
						height="34"
						width="38"
						min-width="38"
						color="success-light-color white--text"
						class="me-1"
						@click="
							currencyId = item.id;
							currency.name = item.name;
							exchangeValue = item.latest_exchange_record;
							updateEquivalentPriceDialog = true;
						"
					>
						<v-icon>mdi-sync</v-icon>
					</v-btn>
				</tooltip>

				<!-- history -->
				<tooltip top :text="$t('tooltip.history')">
					<v-btn
						height="34"
						width="38"
						min-width="38"
						color="preview-color white--text"
						class="me-2"
						@click="
							currencyId = item.id;
							exchangeRecordsHistoryDialog = true;
							fetchExchangeRecordsHistory();
						"
					>
						<v-icon>mdi-book-sync</v-icon>
					</v-btn>
				</tooltip>

				<!-- delete -->
				<tooltip top :text="$t('tooltip.delete')"> 
					<v-btn
						height="34"
						width="38"
						min-width="38"
						color="danger-dark-color white--text"
						class="me-1"
						@click="currencyId = item.id; deleteDialog = true;"
					>
						<v-icon>mdi-delete</v-icon>
					</v-btn>
				</tooltip>
			</template>
		</bee-handy-table>
	</v-container>
</template>
<script>
import rules from '@/validation';
import actions from '@/permissions/actions-names.js'
import { mapGetters, mapState } from 'vuex';
import ls from "@/settings/localStorageExtension";

export default {
	name: 'Currencies',

	data: () => ({
		loading: false,
		dialog: false,

		submitLoading: false,
		
		exchangeRecordsHistoryDialog: false,
		exchangeRecordsHistoryLoading: false,
		exchangeRecords: [],

		updateEquivalentPriceDialog: false,
		updateEquivalentPriceLoading: false,

		actions,
		exchangeValue: null,
		createdAt: null,

		currencyId: null,
		currency: {
			id: null,
			name: null,
			symbol: null,
			latestExchangeRecord: null,
		},
		rules: rules.rules,
		currentFinancialFileId: ls.getLocalFinancialFileId(),
		deleteDialog: false
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.currencyId) {
					const currency = this.getCurrencyById(this.currencyId);
					this.currency.name = currency.name;
					this.currency.symbol = currency.symbol;
					this.currency.latestExchangeRecord = currency.latest_exchange_record;
				}
			} else {
				this.$refs.form.reset();
				this.currencyId = null;
				this.currency.latestExchangeRecord = null;
			}
		},
		updateEquivalentPriceDialog(val) {
			if (!val) this.currencyId = null;
		},
		exchangeRecordsHistoryDialog(val) {
			if (!val) this.exchangeRecords = [];
		}
	},

	computed: {
		...mapState({
			configs: state => state.configs.configs,
			currencies: state => state.currencies.currencies,
		}),
		...mapGetters({
			getCurrencyById: 'currencies/getCurrencyById',
		}),

		historyHeaders(){
			return [ 
				{
					text: this.$t('CurrenciesPage.history-headers.equivalent-price-with-system-currency'),
					value: 'exchange_value',
					type: 'string',
					align: 'center',
					width: '50%'
				},
				{
					text: this.$t('CurrenciesPage.history-headers.update-date'),
					value: 'created_at',
					type: 'date',
					align: 'center',
					width: '50%'
				}
			]
		},
		headers() {
			return [
				{
					text: this.$t('CurrenciesPage.headers.currency-symbol'),
					value: 'symbol',
					name: 'symbol',
					type: 'string',
					width: '30%'
				},
				{
					text: this.$t('CurrenciesPage.headers.currency-name'),
					value: 'name',
					name: 'name',
					type: 'string',
					width: '30%'
				},
				{
					text: this.$t('CurrenciesPage.headers.equivalent-price'),
					value: 'latest_exchange_record',
					name: 'latestExchangeRecord',
					type: 'string',
					width: '20%'
				},
				{
					text: this.$t('CurrenciesPage.headers.actions'),
					value: 'actions',
					name: 'actions',
					type: 'actions',
					width: '20%',
				}
			]
		},

		isFileLocked(){
			if(this.currentFinancialFileId) {
				return this.configs?.financial_file?.is_locked
			} else {
				return null
			}
		}
	},
	methods: {
		save() {
			if (!this.$refs.form.validate()) return;
			const actionName = !this.currencyId ? 'create' : 'update';
		
			this.submitLoading = true;
			this.$store.dispatch(`currencies/${actionName}`,{
				id: this.currencyId,
				name: this.currency.name,
				symbol: this.currency.symbol,
				latestExchangeRecord: +this.currency.latestExchangeRecord
			}).then(() => {
				Promise.all([
					this.$store.dispatch('currencies/fetchAll'),
					this.currentFinancialFileId ? this.$store.dispatch('configs/fetchAll') : null
				]);
				this.dialog = false;
			}).finally(() => {
				this.submitLoading = false;
			});
		},
		
		fetchExchangeRecordsHistory() {
			this.exchangeRecordsHistoryLoading = true;
			this.$store.dispatch('currencies/fetchExchangeRecords', this.currencyId).then((data) => {
				this.exchangeRecords = data;
				this.exchangeRecordsHistoryDialog = true
			}).finally(() => {
				this.exchangeRecordsHistoryLoading = false;
			})
		},

		createExchangeRecord(){
			if (!this.$refs.equivalentPriceForm.validate()) return;

			this.updateEquivalentPriceLoading = true;
			this.$store.dispatch('currencies/createExchangeRecord', {
				id: this.currencyId,
				exchangeValue: +this.exchangeValue,
				createdAt: this.createdAt
			}).then(() => {
				this.updateEquivalentPriceDialog = false;
				this.updateEquivalentPriceLoading = false;
			}).finally(() => {
				this.loading = true;
				Promise.all([
					this.$store.dispatch('currencies/fetchAll'),
					this.currentFinancialFileId ? this.$store.dispatch('configs/fetchAll') : null
				]).finally(() => {
					this.loading = false;
				});
			});
		},
		confirmDeleteCurrency() {
			// console.log(this.currencyId)
            this.loading = true
            this.$store.dispatch('currencies/deleteCurrency', this.currencyId ).then(() => {
				Promise.all([
					this.$store.dispatch('currencies/fetchAll'),
					this.currentFinancialFileId ? this.$store.dispatch('configs/fetchAll') : null
				]).finally(() => {
					this.loading = false
					this.deleteDialog = false
				})
            }).catch(() => {
                this.loading = false
            })
        },
	},
	created() {
		this.loading = true;
		Promise.all([
			!this.currencies.length ? this.$store.dispatch('currencies/fetchAll') : null,
			Object.keys(this.configs).length === 0 && this.currentFinancialFileId ? this.$store.dispatch('configs/fetchAll') : null
		]).finally(() => {
			this.loading = false;
		})
	}
};
</script>